import { useEffect, useMemo, useState } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { asBasisPoints, asMoney, asQuantity } from '@core/money';
import { computeDocument } from '@core/calc';
import { financialYearFor } from '@domain/financialYear';
import { formatMoney } from '@core/money/format';
import { parseMoney, parseQuantity } from '@core/money';
import { asMoney as moneyValue } from '@core/money/types';
import { getDb } from '@data/db/database';
import { resolveActiveBusiness } from '@data/services/businessSession';
import { toast } from '@ui/components/Toaster';
import { DocumentScreen } from '@features/documents/DocumentScreen';
import { useSalesOrder, useSalesOrderOptions, useCreateSalesOrder, useUpdateSalesOrder, useDeleteSalesOrder } from './useSalesOrders';
import type { SalesOrderLineInput } from '@data/services/salesOrderService';
import { STANDARD_UNITS } from '@features/masterData/units';

interface OrderLineState {
  readonly id: string;
  readonly productId: string;
  readonly quantity: string;
  readonly rate: string;
  readonly description: string;
  readonly unit: string;
  readonly sourceLineId?: string;
  readonly taxRateBp?: number;
  readonly cessRateBp?: number;
}
interface OrderFormState {
  readonly customerId: string;
  readonly docDate: string;
  readonly lines: readonly OrderLineState[];
  readonly roundOff: boolean;
  readonly terms: string;
  readonly notes: string;
  readonly placeOfSupplyOverride: string;
}

let lineCounter = 0;
const nextLineId = () => `so-line-${++lineCounter}`;
const inputClass = 'w-full rounded border border-slate-700 bg-slate-900 px-2 py-1.5 text-sm text-slate-100';
const today = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
};
const blankState = (): OrderFormState => ({
  customerId: '', docDate: today(), lines: [{ id: nextLineId(), productId: '', quantity: '1', rate: '', description: '', unit: 'NOS' }],
  roundOff: false, terms: '', notes: '', placeOfSupplyOverride: '',
});

function totalsFor(input: {
  business: NonNullable<Awaited<ReturnType<typeof resolveActiveBusiness>>>;
  customer: { stateCode: string; shippingStateCode?: string } | null;
  state: OrderFormState;
  productsById: ReadonlyMap<string, { product: { name: string; unit: string; cessRateBp: number }; taxRateBp: number | null }>;
}) {
  const { business, customer, state, productsById } = input;
  const lines = state.lines.filter((line) => line.productId && productsById.has(line.productId));
  if (!lines.length) return null;
  return computeDocument({
    business: { stateCode: business.stateCode, currency: 'INR' },
    docType: 'SALES_ORDER',
    docDate: state.docDate,
    financialYear: financialYearFor(state.docDate),
    placeOfSupplyCode: state.placeOfSupplyOverride || customer?.shippingStateCode || customer?.stateCode || '',
    reverseCharge: false,
    lines: lines.map((line) => {
      const product = productsById.get(line.productId);
      const quantity = Number(line.quantity);
      const rate = Number(line.rate);
      return {
        productId: line.productId,
        description: line.description.trim() || product?.product.name || 'Item',
        unit: product?.product.unit ?? 'NOS',
        quantity: asQuantity(Math.round((Number.isFinite(quantity) ? quantity : 0) * 1000)),
        freeQuantity: asQuantity(0),
        ratePaise: asMoney(Math.round((Number.isFinite(rate) ? rate : 0) * 100)),
        discountType: 'NONE' as const,
        discountValue: 0,
        taxRateBp: asBasisPoints(line.taxRateBp ?? product?.taxRateBp ?? 0),
        cessRateBp: asBasisPoints(line.cessRateBp ?? product?.product.cessRateBp ?? 0),
      };
    }),
    charges: [], invoiceDiscount: null, roundOff: state.roundOff,
    roundOffMode: business.documentSettings.roundOffMode, tcs: null,
  });
}

function toServiceLines(lines: readonly OrderLineState[]): SalesOrderLineInput[] {
  return lines.map((line) => ({
    productId: line.productId,
    quantityMilli: parseQuantity(line.quantity, 3),
    ratePaise: parseMoney(line.rate),
    unit: line.unit,
    ...(line.description.trim() ? { description: line.description.trim() } : {}),
    ...(line.sourceLineId ? { sourceLineId: line.sourceLineId } : {}),
  }));
}

export function SalesOrderEditorScreen({ documentId = null }: { documentId?: string | null }) {
  const options = useSalesOrderOptions();
  const existing = useSalesOrder(documentId);
  const create = useCreateSalesOrder();
  const update = useUpdateSalesOrder();
  const remove = useDeleteSalesOrder();
  const [business, setBusiness] = useState<Awaited<ReturnType<typeof resolveActiveBusiness>> | null>(null);
  const [state, setState] = useState<OrderFormState>(blankState);
  const [loadedId, setLoadedId] = useState<string | null>(null);
  useEffect(() => { void resolveActiveBusiness(getDb() as never).then(setBusiness).catch(() => setBusiness(null)); }, []);

  const products = useMemo(() => options.data?.products ?? [], [options.data?.products]);
  const customers = options.data?.customers ?? [];
  const productsById = useMemo(() => new Map(products.map((entry) => [entry.product.id, entry])), [products]);
  if (documentId && existing.data && existing.data.stored.status === 'DRAFT' && loadedId !== documentId) {
    const linksByLineId = existing.data.sourceLineByOrderLineId;
    setState({
      customerId: existing.data.stored.customerId ?? '',
      docDate: existing.data.stored.docDate,
      lines: existing.data.lines.map((line) => ({
        id: nextLineId(), productId: line.productId ?? '', quantity: String(line.quantityMilli / 1000),
        rate: String(line.ratePaise / 100), description: line.description, unit: line.unit,
        ...(linksByLineId.get(line.id) ? { sourceLineId: linksByLineId.get(line.id) } : {}),
        taxRateBp: line.taxRateBp, cessRateBp: line.cessRateBp,
      })),
      roundOff: existing.data.stored.roundOffPaise !== 0,
      terms: existing.data.stored.terms ?? '', notes: existing.data.stored.notes ?? '',
      placeOfSupplyOverride: existing.data.stored.placeOfSupplyCode,
    });
    setLoadedId(documentId);
  }
  const editing = documentId !== null;
  if (editing && existing.data && existing.data.stored.status !== 'DRAFT') return <DocumentScreen documentId={documentId} />;
  const customer = customers.find((entry) => entry.id === state.customerId) ?? null;
  const totals = business ? totalsFor({ business, customer, state, productsById }) : null;

  async function save() {
    const valid = state.docDate && customer && state.lines.length > 0 && state.lines.every((line) => {
      try { return line.productId !== '' && Number(parseQuantity(line.quantity, productsById.get(line.productId)?.product.unitPrecision ?? 3)) > 0 && Number(parseMoney(line.rate)) >= 0; }
      catch { return false; }
    });
    if (!business || !valid) {
      toast({ title: 'Complete the sales order details', tone: 'danger' });
      return;
    }
    try {
      const payload = {
        customerId: state.customerId,
        docDate: state.docDate,
        lines: toServiceLines(state.lines),
        roundOff: state.roundOff,
        terms: state.terms,
        notes: state.notes,
        placeOfSupplyOverride: state.placeOfSupplyOverride,
      };
      const result = editing && documentId
        ? await update.mutateAsync({ id: documentId, input: payload })
        : await create.mutateAsync({ ...payload, businessId: business.id });
      toast({ title: `Sales order ${result.docNumber} saved as draft`, tone: 'success' });
      if (!editing) window.location.hash = `#/sales/orders/${result.id}/edit`;
    } catch (error) {
      toast({ title: 'Could not save sales order', description: error instanceof Error ? error.message : 'Try again.', tone: 'danger' });
    }
  }

  async function deleteDraft() {
    if (!documentId || !window.confirm('Delete this sales order draft?')) return;
    try {
      await remove.mutateAsync(documentId);
      toast({ title: 'Sales order draft deleted', tone: 'success' });
      window.location.hash = '#/sales/orders';
    } catch (error) {
      toast({ title: 'Could not delete sales order', description: error instanceof Error ? error.message : 'Try again.', tone: 'danger' });
    }
  }

  if (options.isLoading || (editing && existing.isLoading) || !business) return <p className="p-4 text-sm text-slate-400">Loading sales order…</p>;
  return <div className="flex flex-col gap-4 p-4 text-slate-100">
    <header><h1 className="text-lg font-semibold">{editing ? 'Edit sales order draft' : 'New sales order'}</h1><p className="text-xs text-slate-400">Orders do not move stock, create payments or post receivables. Fulfilment is recorded later with Delivery Notes.</p></header>
    {editing && existing.data && <p data-testid="sales-order-number" className="text-sm">{existing.data.stored.docNumber}{existing.data.stored.quotationId ? ' · converted from quotation' : ''}</p>}
    {!customers.length || !products.length ? <p className="text-sm text-slate-400">Add a customer and at least one product before creating a sales order.</p> : <div className="grid gap-4 xl:grid-cols-[1fr_19rem]">
      <section className="space-y-3">
        <div className="grid gap-3 sm:grid-cols-2">
          <label className="text-xs text-slate-400">Customer<select data-testid="sales-order-customer" className={inputClass} value={state.customerId} onChange={(event) => setState((old) => ({ ...old, customerId: event.target.value }))}><option value="">Choose customer…</option>{customers.map((entry) => <option key={entry.id} value={entry.id}>{entry.name}</option>)}</select></label>
          <label className="text-xs text-slate-400">Date<input data-testid="sales-order-date" type="date" className={inputClass} value={state.docDate} onChange={(event) => setState((old) => ({ ...old, docDate: event.target.value }))} /></label>
        </div>
        <div className="overflow-x-auto"><table className="w-full text-sm"><thead className="text-left text-xs uppercase text-slate-400"><tr><th>Product / description</th><th>Qty</th><th>Unit</th><th>Rate</th><th></th></tr></thead><tbody>{state.lines.map((line) => <tr key={line.id} className="border-t border-slate-800"><td className="py-2 pr-2"><select data-testid="sales-order-product" className={inputClass} value={line.productId} onChange={(event) => { const product = productsById.get(event.target.value); setState((old) => ({ ...old, lines: old.lines.map((item) => item.id === line.id ? { ...item, productId: event.target.value, rate: product ? String(product.product.salePricePaise / 100) : '', unit: product?.product.unit ?? 'NOS', sourceLineId: undefined, taxRateBp: undefined, cessRateBp: undefined } : item) })); }}><option value="">Choose product…</option>{products.map((entry) => <option key={entry.product.id} value={entry.product.id}>{entry.product.name}</option>)}</select><input className={`${inputClass} mt-1`} aria-label="Sales order line description" placeholder="Description" value={line.description} onChange={(event) => setState((old) => ({ ...old, lines: old.lines.map((item) => item.id === line.id ? { ...item, description: event.target.value } : item) }))} /></td><td><input data-testid="sales-order-quantity" className={inputClass} inputMode="decimal" value={line.quantity} onChange={(event) => setState((old) => ({ ...old, lines: old.lines.map((item) => item.id === line.id ? { ...item, quantity: event.target.value } : item) }))} /></td><td><select data-testid="sales-order-unit" className={inputClass} value={line.unit} onChange={(event) => setState((old) => ({ ...old, lines: old.lines.map((item) => item.id === line.id ? { ...item, unit: event.target.value } : item) }))}>{STANDARD_UNITS.map(u=><option key={u.code} value={u.code}>{u.label}</option>)}</select></td><td><input data-testid="sales-order-rate" className={inputClass} inputMode="decimal" value={line.rate} onChange={(event) => setState((old) => ({ ...old, lines: old.lines.map((item) => item.id === line.id ? { ...item, rate: event.target.value } : item) }))} /></td><td><button type="button" aria-label="Remove sales order line" onClick={() => setState((old) => ({ ...old, lines: old.lines.filter((item) => item.id !== line.id) }))}><Trash2 className="h-4 w-4" /></button></td></tr>)}</tbody></table></div>
        <button type="button" className="rounded border border-slate-700 px-3 py-2 text-sm" onClick={() => setState((old) => ({ ...old, lines: [...old.lines, { id: nextLineId(), productId: '', quantity: '1', rate: '', description: '', unit: 'NOS' }] }))}><Plus className="mr-1 inline h-4 w-4" />Add line</button>
        <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={state.roundOff} onChange={(event) => setState((old) => ({ ...old, roundOff: event.target.checked }))} />Round off total</label>
        <label className="block text-xs text-slate-400">Terms<textarea data-testid="sales-order-terms" className={`${inputClass} mt-1`} rows={3} value={state.terms} onChange={(event) => setState((old) => ({ ...old, terms: event.target.value }))} /></label>
        <label className="block text-xs text-slate-400">Notes<textarea data-testid="sales-order-notes" className={`${inputClass} mt-1`} rows={3} value={state.notes} onChange={(event) => setState((old) => ({ ...old, notes: event.target.value }))} /></label>
      </section>
      <aside className="h-fit rounded border border-slate-800 p-4"><h2 className="mb-3 text-xs uppercase text-slate-400">Sales order total</h2><div className="flex justify-between border-t border-slate-700 py-2 font-semibold"><span>Total</span><span data-testid="sales-order-total">{totals ? formatMoney(moneyValue(totals.grandTotalPaise)) : '—'}</span></div><button type="button" data-testid="sales-order-save" disabled={!totals || create.isPending || update.isPending} onClick={() => void save()} className="mt-3 w-full rounded bg-brand-500 px-3 py-2 text-sm font-medium disabled:opacity-40">{editing ? 'Save changes' : 'Save draft'}</button>{editing && documentId && <><a className="mt-2 block rounded border border-slate-700 px-3 py-2 text-center text-sm" href={`#/sales/orders/${documentId}`}>Preview without saving</a><button type="button" data-testid="delete-sales-order-draft" className="mt-2 w-full rounded border border-red-900 px-3 py-2 text-sm text-red-300" disabled={remove.isPending} onClick={() => void deleteDraft()}>Delete draft</button></>}<a className="mt-2 block text-center text-sm text-slate-400" href="#/sales/orders">Back to sales orders</a></aside>
    </div>}
  </div>;
}
